import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface YouchuanUpscaleParams {
  /**
   * 作业ID，必填
   */
  jobId: string;
  /**
   * 图像编号，必填，范围0-3
   * @values 0 | 1 | 2 | 3
   */
  imageNo: 0 | 1 | 2 | 3;
  /**
   * 放大类型，必填，0(标准)、1(创意)、2(v5_2x)、3(v5_4x)
   * @values 0 | 1 | 2 | 3
   */
  type: 0 | 1 | 2 | 3;
  /**
   * 回调URL，必须以http://或https://开头
   */
  callback?: string;
}

export interface YouchuanUpscaleResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * youchuan_upscale
 *
 * 通过悠船图像放大API创建图像放大任务。
 *
 * API Documentation: https://tob.youchuan.cn/openapi/apis/%E5%9B%BE%E7%89%87%E7%94%9F%E6%88%90/api_upscale
 *
 * @example
 * ```typescript
 * import { initSeacloud, youchuanUpscale } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await youchuanUpscale({
 *   jobId: 'your-job-id',
 *   imageNo: 0,
 *   type: 0
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 YouchuanUpscaleParams 接口定义
 * @returns 生成的资源列表
 */
export async function youchuanUpscale(
  params: YouchuanUpscaleParams,
): Promise<YouchuanUpscaleResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'youchuan_upscale',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: YouchuanUpscaleResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
